import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import vendorApi from '../../services/vendorApi';
import { imageAPI, imageUtils } from '../../services/imageApi';
import { isManagedMediaUrl } from '../../utils/mediaImageUtils';
import { 
  ArrowLeft, 
  Plus, 
  Edit, 
  Trash2, 
  Upload,
  X,
  Save,
  MapPin,
  Star,
  Camera,
  Image as ImageIcon,
  Bed,
  ChevronRight
} from 'lucide-react';
import { roomTaxesFromDiscounted } from '../../utils/roomPricing';
import { normalizeRoomPriceForSave, minDiscountedAcrossWeek } from '../../utils/roomDayPricing';
import { DEFAULT_ROOM_FORM_OPTIONS, mergeRoomFormOptions } from '../../utils/roomBookingOptions';
import RoomWeekdayPricingFields from '../../components/admin/RoomWeekdayPricingFields';
import RoomGuestPricingFields from '../../components/admin/RoomGuestPricingFields';
import DraggableStayImagesGrid from '../../components/DraggableStayImagesGrid';
import ResponsiveImage from '../../components/ResponsiveImage';
import PawStoryVideoUpload from '../../components/PawStoryVideoUpload';
import ListingAttributesEditor from '../../components/ListingAttributesEditor';
import {
  createEmptyListingAttributes,
  mergeListingAttributes
} from '../../config/listingAttributesModel';

const VendorListingDetails = () => {
  const navigate = useNavigate();
  const { stayId } = useParams();
  const { user, loading: authLoading, isAuthenticated } = useAuth();
  const isNew = stayId === 'new';
  const isEdit = !isNew;

  const [loading, setLoading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadingFounderReviewSlide, setUploadingFounderReviewSlide] = useState(null);
  const [imagePreviewUrls, setImagePreviewUrls] = useState([]);
  const [uploadingRoomImages, setUploadingRoomImages] = useState(false);
  const [roomImagePreviewUrls, setRoomImagePreviewUrls] = useState([]);
  const [showRooms, setShowRooms] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [editingRoom, setEditingRoom] = useState(null);
  const [showRoomForm, setShowRoomForm] = useState(false);

  const [formData, setFormData] = useState({
    stayId: '',
    title: '',
    address: '',
    city: '',
    state: '',
    latitude: '',
    longitude: '',
    rating: 4.5,
    ratingTag: 'Very Good',
    founderVerified: false,
    inSpotlight: false,
    description: '',
    images: [],
    features: [],
    houseRules: [],
    nearestLandmarks: [],
    amenities: [],
    founderReviews: [],
    listingAttributes: createEmptyListingAttributes(),
    pawStoryVideoUrl: '',
    cancellationPolicy: ''
  });

  const [newFeature, setNewFeature] = useState({ title: '', image: '' });
  const [newRule, setNewRule] = useState({ ruleName: '', ruleValue: '' });
  const [newLandmark, setNewLandmark] = useState({ location: '', distance: '' });
  const [featureImageFile, setFeatureImageFile] = useState(null);
  const [uploadingFeatureImage, setUploadingFeatureImage] = useState(false);
  const [featureImagePreview, setFeatureImagePreview] = useState(null);
  const [useImageUpload, setUseImageUpload] = useState(false);
  const [newAmenity, setNewAmenity] = useState('');

  const [roomFormData, setRoomFormData] = useState({
    stayId: '',
    roomId: '',
    title: '',
    description: '',
    capacity: 2,
    beds: 1,
    features: [],
    options: { ...DEFAULT_ROOM_FORM_OPTIONS },
    price: {
      mrp: 0,
      discountedPrice: 0,
      taxes: 0,
      currency: 'INR',
      useDayOfWeekPricing: false,
      dayPricing: {},
      guestPricing: { enabled: false, tiers: [], thresholdAdults: 2, extraAdultAddon: { mrp: 0, discountedPrice: 0 } }
    },
    images: [],
    amenities: []
  });

  useEffect(() => {
    if (!authLoading) {
      if (!isAuthenticated || !user) {
        navigate('/vendor/login', { replace: true });
        return;
      }
      if (user.role !== 'vendor' && user.role !== 'admin') {
        navigate('/vendor/login', { replace: true, state: { needsVendorAccount: true } });
        return;
      }
    }
  }, [user, authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (isEdit && stayId && stayId !== 'new') {
      fetchStayDetails();
    } else if (isNew) {
      // Reset form for new stay
      const defaultStayId = `s_${Date.now().toString().slice(-8)}`;
      setFormData({
        stayId: defaultStayId,
        title: '',
        address: '',
        city: '',
        state: '',
        latitude: '',
        longitude: '',
        rating: 4.5,
        ratingTag: 'Very Good',
        founderVerified: false,
        inSpotlight: false,
        description: '',
        images: [],
        features: [],
        houseRules: [],
        nearestLandmarks: [],
        amenities: [],
        founderReviews: [],
        listingAttributes: createEmptyListingAttributes(),
        pawStoryVideoUrl: '',
        cancellationPolicy: ''
      });
      setRooms([]);
    }
  }, [stayId]);

  const fetchStayDetails = async () => {
    try {
      setLoading(true);
      const stay = await vendorApi.getStay(stayId);
      
      // Ensure all fields are properly initialized with defaults if missing
      setFormData({
        stayId: stay.stayId || '',
        title: stay.title || '',
        address: stay.address || '',
        city: stay.city || '',
        state: stay.state || '',
        latitude: stay.latitude != null ? stay.latitude.toString() : '',
        longitude: stay.longitude != null ? stay.longitude.toString() : '',
        rating: stay.rating != null ? stay.rating : 4.5,
        ratingTag: stay.ratingTag || 'Very Good',
        founderVerified: stay.founderVerified || false,
        inSpotlight: stay.inSpotlight || false,
        description: stay.description || '',
        images: Array.isArray(stay.images) ? stay.images : [],
        features: Array.isArray(stay.features) ? stay.features : [],
        houseRules: Array.isArray(stay.houseRules) ? stay.houseRules : [],
        nearestLandmarks: Array.isArray(stay.nearestLandmarks) ? stay.nearestLandmarks : [],
        amenities: Array.isArray(stay.amenities) ? stay.amenities : [],
        founderReviews: Array.isArray(stay.founderReviews) ? stay.founderReviews : [],
        listingAttributes: mergeListingAttributes(stay.listingAttributes),
        pawStoryVideoUrl: stay.pawStoryVideoUrl || '',
        cancellationPolicy: stay.cancellationPolicy || ''
      });
      
      // Fetch rooms separately
      try {
        const roomsResponse = await vendorApi.getRooms(stayId);
        setRooms(roomsResponse.rooms || []);
      } catch (roomError) {
        console.error('Error fetching rooms:', roomError);
        // If rooms are included in stay data, use those
        if (stay.rooms && Array.isArray(stay.rooms)) {
          setRooms(stay.rooms);
        } else {
          setRooms([]);
        }
      }
    } catch (error) {
      console.error('Error fetching stay details:', error);
      alert('Failed to load stay details. Please try again.');
      navigate('/vendor/listings');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    let previewUrls = [];
    try {
      setUploadingImages(true);
      previewUrls = files
        .filter((f) => f.type.startsWith('image/'))
        .map((file) => imageUtils.createPreviewUrl(file));
      if (previewUrls.length) setImagePreviewUrls((prev) => [...prev, ...previewUrls]);

      const { urls, errors } = await imageAPI.uploadGalleryMedia(files, 'stays');
      if (errors.length) alert(`Upload notes:\n${errors.join('\n')}`);
      if (urls.length) {
        setFormData((prev) => ({ ...prev, images: [...prev.images, ...urls] }));
      } else if (errors.length) {
        throw new Error('No files uploaded');
      }

      previewUrls.forEach((url) => imageUtils.revokePreviewUrl(url));
      setImagePreviewUrls([]);
    } catch (error) {
      console.error('Gallery upload error:', error);
      const details =
        error.response?.data?.details || error.response?.data?.error || error.message;
      alert(details ? `Failed to upload gallery media: ${details}` : 'Failed to upload gallery media.');
      previewUrls.forEach((url) => imageUtils.revokePreviewUrl(url));
      setImagePreviewUrls([]);
    } finally {
      setUploadingImages(false);
      event.target.value = '';
    }
  };

  const reorderStayImages = (nextUrls) => {
    setFormData((prev) => ({ ...prev, images: nextUrls }));
  };

  const handleRoomImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    const validFiles = [];
    const errors = [];

    files.forEach((file) => {
      if (!imageUtils.isValidImageType(file)) {
        errors.push(`${file.name}: Invalid file type.`);
        return;
      }
      if (!imageUtils.isValidFileSize(file)) {
        errors.push(`${file.name}: File too large.`);
        return;
      }
      validFiles.push(file);
    });

    if (errors.length > 0) {
      alert('Upload errors:\n' + errors.join('\n'));
      return;
    }

    if (validFiles.length === 0) return;

    let previewUrls = [];
    try {
      setUploadingRoomImages(true);
      previewUrls = validFiles.map((file) => imageUtils.createPreviewUrl(file));
      setRoomImagePreviewUrls((prev) => [...prev, ...previewUrls]);

      const response = await imageAPI.uploadImages(validFiles, 'rooms');

      if (response.uploaded && response.uploaded.length > 0) {
        const uploadedUrls = response.uploaded.map((item) => item.url);
        setRoomFormData((prev) => ({
          ...prev,
          images: [...prev.images, ...uploadedUrls]
        }));

        previewUrls.forEach((url) => imageUtils.revokePreviewUrl(url));
        setRoomImagePreviewUrls([]);
      }
    } catch (error) {
      console.error('Room image upload error:', error);
      alert('Failed to upload images.');
      previewUrls.forEach((url) => imageUtils.revokePreviewUrl(url));
      setRoomImagePreviewUrls([]);
    } finally {
      setUploadingRoomImages(false);
      event.target.value = '';
    }
  };

  const reorderRoomImages = (nextUrls) => {
    setRoomFormData((prev) => ({ ...prev, images: nextUrls }));
  };

  const removeRoomImage = async (index) => {
    const imageUrl = roomFormData.images[index];
    if (imageUrl && isManagedMediaUrl(imageUrl)) {
      try {
        await imageAPI.deleteImage(imageUrl);
      } catch (error) {
        console.error('Failed to delete image:', error);
      }
    }
    setRoomFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const removeImage = async (index) => {
    const imageUrl = formData.images[index];
    if (imageUrl && isManagedMediaUrl(imageUrl)) {
      try {
        await imageAPI.deleteImage(imageUrl);
      } catch (error) {
        console.error('Failed to delete image:', error);
      }
    }
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const addFeature = () => {
    if (newFeature.title && newFeature.image) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, { ...newFeature }]
      }));
      setNewFeature({ title: '', image: '' });
      setFeatureImageFile(null);
      setFeatureImagePreview(null);
      setUseImageUpload(false);
    }
  };

  const handleFeatureImageUpload = async (file) => {
    if (!file) return;
    if (!imageUtils.isValidImageType(file)) {
      alert('Please select a valid image file');
      return;
    }
    if (!imageUtils.isValidFileSize(file)) {
      alert('File size must be less than 10MB');
      return;
    }

    try {
      setUploadingFeatureImage(true);
      const previewUrl = imageUtils.createPreviewUrl(file);
      setFeatureImagePreview(previewUrl);

      const response = await imageAPI.uploadImages([file], 'features');
      if (response.uploaded && response.uploaded.length > 0) {
        setNewFeature(prev => ({ ...prev, image: response.uploaded[0].url }));
        imageUtils.revokePreviewUrl(previewUrl);
        setFeatureImagePreview(null);
      }
    } catch (error) {
      console.error('Feature image upload error:', error);
      alert('Failed to upload image.');
      if (featureImagePreview) {
        imageUtils.revokePreviewUrl(featureImagePreview);
        setFeatureImagePreview(null);
      }
    } finally {
      setUploadingFeatureImage(false);
    }
  };

  const handleFeatureImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFeatureImageFile(file);
      handleFeatureImageUpload(file);
    }
  };

  const removeFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const addRule = () => {
    if (newRule.ruleName && newRule.ruleValue) {
      setFormData(prev => ({
        ...prev,
        houseRules: [...prev.houseRules, { ...newRule }]
      }));
      setNewRule({ ruleName: '', ruleValue: '' });
    }
  };

  const removeRule = (index) => {
    setFormData(prev => ({
      ...prev,
      houseRules: prev.houseRules.filter((_, i) => i !== index)
    }));
  };

  const addLandmark = () => {
    if (newLandmark.location && newLandmark.distance) {
      setFormData(prev => ({
        ...prev,
        nearestLandmarks: [...prev.nearestLandmarks, { ...newLandmark }]
      }));
      setNewLandmark({ location: '', distance: '' });
    }
  };

  const removeLandmark = (index) => {
    setFormData(prev => ({
      ...prev,
      nearestLandmarks: prev.nearestLandmarks.filter((_, i) => i !== index)
    }));
  };

  const addAmenity = () => {
    const amenityToAdd = newAmenity.trim();
    if (amenityToAdd && !formData.amenities.includes(amenityToAdd)) {
      setFormData(prev => ({
        ...prev,
        amenities: [...prev.amenities, amenityToAdd]
      }));
      setNewAmenity('');
    }
  };

  const removeAmenity = (index) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.filter((_, i) => i !== index)
    }));
  };

  const handleFounderReviewImageUpload = async (slideIndex, event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!imageUtils.isValidImageType(file)) {
      alert('Please select a valid image file (JPEG, PNG, WebP, GIF).');
      return;
    }
    if (!imageUtils.isValidFileSize(file)) {
      alert('File is too large. Maximum size is 10MB.');
      return;
    }
    try {
      setUploadingFounderReviewSlide(slideIndex);
      const response = await imageAPI.uploadImages([file], 'stays');
      if (response.uploaded && response.uploaded.length > 0) {
        const url = response.uploaded[0].url;
        setFormData((prev) => {
          const next = [...(prev.founderReviews || [])];
          next[slideIndex] = { ...next[slideIndex], image: url };
          return { ...prev, founderReviews: next };
        });
      } else {
        throw new Error('No URL returned');
      }
    } catch (error) {
      console.error('Founder review image upload:', error);
      const details =
        error.response?.data?.details || error.response?.data?.error || error.message;
      alert(details ? `Upload failed: ${details}` : 'Failed to upload image.');
    } finally {
      setUploadingFounderReviewSlide(null);
    }
  };

  const clearFounderReviewImage = (slideIndex) => {
    setFormData((prev) => {
      const next = [...(prev.founderReviews || [])];
      next[slideIndex] = { ...next[slideIndex], image: '' };
      return { ...prev, founderReviews: next };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const submitData = {
        ...formData,
        latitude: formData.latitude === '' ? null : (formData.latitude ? parseFloat(formData.latitude) : null),
        longitude: formData.longitude === '' ? null : (formData.longitude ? parseFloat(formData.longitude) : null),
        founderReviews: (formData.founderReviews || [])
          .filter(
            (r) =>
              (r.quote && String(r.quote).trim()) || (r.image && String(r.image).trim())
          )
          .map((r) => ({
            quote: (r.quote && String(r.quote).trim()) || '',
            image: (r.image && String(r.image).trim()) || '',
            bubbleText: (r.bubbleText && String(r.bubbleText).trim()) || '',
            borderColor: (r.borderColor && String(r.borderColor).trim()) || ''
          }))
      };
      
      let response;
      if (isEdit) {
        response = await vendorApi.updateStay(stayId, submitData);
      } else {
        response = await vendorApi.createStay(submitData);
      }
      
      console.log('Stay saved successfully:', response);
      navigate('/vendor/listings');
    } catch (error) {
      console.error('Error saving stay:', error);
      alert('Error saving stay. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRoomSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const roomPayload = {
        ...roomFormData,
        stayId: formData.stayId,
        price: normalizeRoomPriceForSave(roomFormData.price, roomFormData.capacity)
      };
      
      let response;
      if (editingRoom) {
        response = await vendorApi.updateRoom(formData.stayId, roomFormData.roomId, roomPayload);
      } else {
        response = await vendorApi.createRoom(roomPayload);
      }
      
      const roomsResponse = await vendorApi.getRooms(formData.stayId);
      setRooms(roomsResponse.rooms || []);
      
      setShowRoomForm(false);
      setEditingRoom(null);
      setRoomFormData({
        stayId: '',
        roomId: '',
        title: '',
        description: '',
        capacity: 2,
        beds: 1,
        features: [],
        options: { ...DEFAULT_ROOM_FORM_OPTIONS },
        price: {
          mrp: 0,
          discountedPrice: 0,
          taxes: 0,
          currency: 'INR',
          useDayOfWeekPricing: false,
          dayPricing: {}
        },
        images: [],
        amenities: []
      });
      setRoomImagePreviewUrls([]);
    } catch (error) {
      console.error('Error saving room:', error);
      alert('Error saving room. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditRoom = (room) => {
    setEditingRoom(room);
    const dp = room?.price?.discountedPrice ?? 0;
    setRoomFormData({
      ...room,
      options: mergeRoomFormOptions(room.options),
      price: normalizeRoomPriceForSave({
        ...room.price,
        useDayOfWeekPricing: Boolean(room.price?.useDayOfWeekPricing),
        dayPricing:
          room.price?.dayPricing && typeof room.price.dayPricing === 'object' ? room.price.dayPricing : {},
        taxes: roomTaxesFromDiscounted(dp)
      })
    });
    setShowRoomForm(true);
  };

  const handleDeleteRoom = async (roomId) => {
    if (window.confirm('Are you sure you want to delete this room?')) {
      try {
        await vendorApi.deleteRoom(formData.stayId, roomId);
        const roomsResponse = await vendorApi.getRooms(formData.stayId);
        setRooms(roomsResponse.rooms || []);
      } catch (error) {
        console.error('Error deleting room:', error);
        alert('Error deleting room.');
      }
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2]">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-[#FF5E4C] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !user || (user.role !== 'vendor' && user.role !== 'admin')) {
    return null;
  }

  // Room Form View
  if (showRoomForm) {
    return (
      <div>
        <div className="flex items-center gap-3 mb-6">
          <button
            type="button"
            onClick={() => {
              roomImagePreviewUrls.forEach((url) => imageUtils.revokePreviewUrl(url));
              setRoomImagePreviewUrls([]);
              setShowRoomForm(false);
              setEditingRoom(null);
            }}
            className="inline-flex items-center justify-center p-2 rounded-full bg-[#FAF7F2] text-[#FF5E4C] border border-[#E5E3DF] hover:bg-[#f5f0e8] shrink-0"
            aria-label="Back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-[20px] font-medium text-black tracking-[-0.02em]">
            {editingRoom ? 'Edit room' : 'Add new room'}
          </h1>
        </div>

        <div>
          <form onSubmit={handleRoomSubmit} className="space-y-4">
            <div className="bg-white rounded-xl p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Room ID *
                </label>
                <input
                  type="text"
                  required
                  value={roomFormData.roomId}
                  onChange={(e) => setRoomFormData(prev => ({ ...prev, roomId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="e.g., r_001"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Room Title *
                </label>
                <input
                  type="text"
                  required
                  value={roomFormData.title}
                  onChange={(e) => setRoomFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="e.g., Deluxe Pet Suite"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Capacity *
                  </label>
                  <input
                    type="number"
                    required
                    min="1"
                    value={roomFormData.capacity}
                    onChange={(e) => setRoomFormData(prev => ({ ...prev, capacity: parseInt(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Beds *
                  </label>
                  <input
                    type="number"
                    required
                    min="1"
                    value={roomFormData.beds}
                    onChange={(e) => setRoomFormData(prev => ({ ...prev, beds: parseInt(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  rows={3}
                  value={roomFormData.description}
                  onChange={(e) => setRoomFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    MRP (₹)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={roomFormData.price.mrp}
                    onChange={(e) => setRoomFormData(prev => ({
                      ...prev,
                      price: { ...prev.price, mrp: parseFloat(e.target.value) || 0 }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Discounted Price (₹) *
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={roomFormData.price.discountedPrice}
                    onChange={(e) => {
                      const discountedPrice = parseFloat(e.target.value) || 0;
                      setRoomFormData((prev) => ({
                        ...prev,
                        price: {
                          ...prev.price,
                          discountedPrice,
                          taxes: roomTaxesFromDiscounted(discountedPrice),
                        },
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Taxes (₹)
                  <span className="font-normal text-gray-500 ml-1">(included, 18%)</span>
                </label>
                <input
                  type="text"
                  readOnly
                  value={roomTaxesFromDiscounted(roomFormData.price.discountedPrice).toFixed(2)}
                  className="w-full px-3 py-2 border border-gray-200 bg-gray-50 text-gray-700 rounded-lg cursor-not-allowed"
                  tabIndex={-1}
                />
              </div>

              <RoomWeekdayPricingFields
                price={roomFormData.price}
                setPrice={(next) => setRoomFormData((prev) => ({ ...prev, price: next }))}
              />
              <RoomGuestPricingFields
                price={roomFormData.price}
                setPrice={(next) => setRoomFormData((prev) => ({ ...prev, price: next }))}
                capacity={roomFormData.capacity}
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Room options
                </label>
                <div className="space-y-3 rounded-lg border border-gray-200 p-4">
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={roomFormData.options.roomOnly}
                      onChange={(e) => setRoomFormData((prev) => ({
                        ...prev,
                        options: { ...prev.options, roomOnly: e.target.checked }
                      }))}
                      className="w-4 h-4 text-[#FF5E4C] border-gray-300 rounded focus:ring-[#FF5E4C]/30"
                    />
                    <span className="text-sm text-gray-700">Room only</span>
                  </label>
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={roomFormData.options.withBreakfast}
                      onChange={(e) => setRoomFormData((prev) => ({
                        ...prev,
                        options: { ...prev.options, withBreakfast: e.target.checked }
                      }))}
                      className="w-4 h-4 text-[#FF5E4C] border-gray-300 rounded focus:ring-[#FF5E4C]/30"
                    />
                    <span className="text-sm text-gray-700">With breakfast</span>
                  </label>
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={roomFormData.options.withSpa}
                      onChange={(e) => setRoomFormData((prev) => ({
                        ...prev,
                        options: { ...prev.options, withSpa: e.target.checked }
                      }))}
                      className="w-4 h-4 text-[#FF5E4C] border-gray-300 rounded focus:ring-[#FF5E4C]/30"
                    />
                    <span className="text-sm text-gray-700">With spa services</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Room photos
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                  <input
                    type="file"
                    multiple
                    accept={imageUtils.galleryVideoAccept}
                    onChange={handleRoomImageUpload}
                    className="hidden"
                    id="room-image-upload-vendor"
                    disabled={uploadingRoomImages}
                  />
                  <label
                    htmlFor="room-image-upload-vendor"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    <Camera className="w-8 h-8 text-gray-400 mb-2" />
                    <span className="text-sm text-gray-600">Upload images</span>
                    {uploadingRoomImages && (
                      <span className="text-xs text-blue-600 mt-1">Uploading...</span>
                    )}
                  </label>
                </div>
                {roomFormData.images.length > 0 && (
                  <div className="mt-4">
                    <DraggableStayImagesGrid
                      images={roomFormData.images}
                      onReorder={reorderRoomImages}
                      onRemove={removeRoomImage}
                      hintText="Drag to reorder. The first image is used as the room cover in galleries and booking."
                      altPrefix="Room"
                      gridClassName="grid grid-cols-2 sm:grid-cols-3 gap-3"
                      imageClassName="w-full h-28 object-cover rounded-lg"
                      removeButtonClassName="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full"
                      hintClassName="text-xs text-gray-500 mb-2"
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="flex space-x-4">
              <button
                type="button"
                onClick={() => {
                  roomImagePreviewUrls.forEach((url) => imageUtils.revokePreviewUrl(url));
                  setRoomImagePreviewUrls([]);
                  setShowRoomForm(false);
                  setEditingRoom(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2 bg-[#FF5E4C] text-white rounded-lg disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save Room'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <button
          type="button"
          onClick={() => navigate('/vendor/listings')}
          className="inline-flex items-center justify-center p-2 rounded-full bg-[#FAF7F2] text-[#FF5E4C] border border-[#E5E3DF] hover:bg-[#f5f0e8] shrink-0"
          aria-label="Back to listings"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-[20px] font-medium text-black tracking-[-0.02em]">
          {isEdit ? 'Listing details' : 'Create new listing'}
        </h1>
      </div>

      <div className="pb-4 md:pb-0">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Basics Section */}
          <div className="bg-white rounded-xl p-4 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Basics</h2>
            
            {/* Stay Name */}
            <div>
              <label className="block text-sm text-gray-500 mb-1">Stay Name</label>
              <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="flex-1 outline-none"
                  placeholder="Enter stay name"
                />
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </div>
            </div>

            {/* Stay Location - Separate Fields */}
            <div>
              <label className="block text-sm text-gray-500 mb-1">Stay Location</label>
              <div className="space-y-2">
                <input
                  type="text"
                  required
                  value={formData.address}
                  onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg outline-none"
                  placeholder="Address"
                />
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    required
                    value={formData.city}
                    onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg outline-none"
                    placeholder="City"
                  />
                  <input
                    type="text"
                    required
                    value={formData.state}
                    onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg outline-none"
                    placeholder="State"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="number"
                    step="any"
                    value={formData.latitude}
                    onChange={(e) => setFormData(prev => ({ ...prev, latitude: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg outline-none"
                    placeholder="Latitude"
                  />
                  <input
                    type="number"
                    step="any"
                    value={formData.longitude}
                    onChange={(e) => setFormData(prev => ({ ...prev, longitude: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg outline-none"
                    placeholder="Longitude"
                  />
                </div>
              </div>
            </div>

            {/* Features (Highlights) - Title + Image pairs */}
            <div>
              <label className="block text-sm text-gray-500 mb-2">Highlights</label>
              <div className="space-y-3">
                {formData.features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-3 p-2 bg-gray-50 rounded-lg">
                    {feature.image && (
                      <ResponsiveImage
                        src={feature.image}
                        variant="thumb48"
                        alt={feature.title}
                        className="h-12 w-12 rounded-lg object-cover object-center"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 text-sm truncate">{feature.title}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFeature(index)}
                      className="p-1 text-red-500"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-3 space-y-2">
                  <input
                    type="text"
                    value={newFeature.title}
                    onChange={(e) => setNewFeature(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="Feature title"
                  />
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={() => setUseImageUpload(!useImageUpload)}
                      className={`px-2 py-1 text-xs rounded ${
                        !useImageUpload ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      URL
                    </button>
                    <button
                      type="button"
                      onClick={() => setUseImageUpload(!useImageUpload)}
                      className={`px-2 py-1 text-xs rounded ${
                        useImageUpload ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      Upload
                    </button>
                  </div>
                  {!useImageUpload ? (
                    <input
                      type="url"
                      value={newFeature.image}
                      onChange={(e) => setNewFeature(prev => ({ ...prev, image: e.target.value }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      placeholder="Image URL"
                    />
                  ) : (
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFeatureImageChange}
                        className="hidden"
                        id="feature-image-upload-mobile"
                      />
                      <label
                        htmlFor="feature-image-upload-mobile"
                        className="block px-2 py-1 border border-gray-300 rounded text-sm text-center cursor-pointer"
                      >
                        {uploadingFeatureImage ? 'Uploading...' : 'Choose Image'}
                      </label>
                      {newFeature.image && (
                        <div className="mt-2">
                          <ResponsiveImage
                            src={newFeature.image}
                            variant="thumb64"
                            alt="Preview"
                            className="h-16 w-16 rounded object-cover object-center"
                          />
                        </div>
                      )}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={addFeature}
                    disabled={!newFeature.title || !newFeature.image || uploadingFeatureImage}
                    className="w-full px-2 py-1 bg-green-600 text-white rounded text-sm disabled:opacity-50"
                  >
                    Add Feature
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Details Section - All fields from AdminStays */}
          <div className="bg-white rounded-xl p-4 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Additional Details</h2>
            
            {/* Stay ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Stay ID *
              </label>
              <input
                type="text"
                required
                value={formData.stayId}
                onChange={(e) => setFormData(prev => ({ ...prev, stayId: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="e.g., s_001"
              />
            </div>

            {/* Rating */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rating (0-5)
                </label>
                <input
                  type="number"
                  min="0"
                  max="5"
                  step="0.1"
                  value={formData.rating}
                  onChange={(e) => setFormData(prev => ({ ...prev, rating: parseFloat(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rating Tag
                </label>
                <input
                  type="text"
                  value={formData.ratingTag}
                  onChange={(e) => setFormData(prev => ({ ...prev, ratingTag: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="e.g., Very Good"
                />
              </div>
            </div>

            {/* About the property */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                About the property
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Shown in the About the property section on the stay detail page.
              </p>
              <textarea
                rows={4}
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="Describe the stay..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Paw Stories reel (optional)
              </label>
              <p className="text-xs text-gray-500 mb-3">
                Upload MP4, MOV, or WebM (up to 500MB raw; server compresses to about 10MB max), or paste a hosted URL.
              </p>
              <PawStoryVideoUpload
                variant="vendor"
                videoUrl={formData.pawStoryVideoUrl || ''}
                onVideoUrlChange={(url) => setFormData((prev) => ({ ...prev, pawStoryVideoUrl: url }))}
              />
            </div>

            {/* Images */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Images
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="image-upload"
                  disabled={uploadingImages}
                />
                <label
                  htmlFor="image-upload"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <Camera className="w-8 h-8 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-600">Upload Images</span>
                  {uploadingImages && (
                    <span className="text-xs text-blue-600 mt-1">Uploading...</span>
                  )}
                </label>
              </div>
              {formData.images.length > 0 && (
                <div className="mt-4">
                  <DraggableStayImagesGrid
                    images={formData.images}
                    onReorder={reorderStayImages}
                    onRemove={removeImage}
                    gridClassName="grid grid-cols-2 sm:grid-cols-3 gap-3"
                    imageClassName="w-full h-28 object-cover rounded-lg"
                    removeButtonClassName="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full"
                    hintClassName="text-xs text-gray-500 mb-2"
                  />
                </div>
              )}
            </div>

            {/* Cancellation Policy */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cancellation Policy
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Shown to guests on the booking review page before payment.
              </p>
              <textarea
                value={formData.cancellationPolicy || ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, cancellationPolicy: e.target.value }))}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                placeholder="Describe cancellation and refund terms for this stay."
              />
            </div>

            {/* House Rules */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                House Rules
              </label>
              <div className="space-y-2 mb-2">
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    value={newRule.ruleName}
                    onChange={(e) => setNewRule(prev => ({ ...prev, ruleName: e.target.value }))}
                    className="px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="Rule name"
                  />
                  <input
                    type="text"
                    value={newRule.ruleValue}
                    onChange={(e) => setNewRule(prev => ({ ...prev, ruleValue: e.target.value }))}
                    className="px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="Rule value"
                  />
                </div>
                <button
                  type="button"
                  onClick={addRule}
                  className="w-full px-2 py-1 bg-green-600 text-white rounded text-sm"
                >
                  Add Rule
                </button>
              </div>
              <div className="space-y-2">
                {formData.houseRules.map((rule, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{rule.ruleName}</p>
                      <p className="text-xs text-gray-500">{rule.ruleValue}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeRule(index)}
                      className="p-1 text-red-500"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Nearest Landmarks */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nearest Landmarks
              </label>
              <div className="space-y-2 mb-2">
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    value={newLandmark.location}
                    onChange={(e) => setNewLandmark(prev => ({ ...prev, location: e.target.value }))}
                    className="px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="Landmark name"
                  />
                  <input
                    type="text"
                    value={newLandmark.distance}
                    onChange={(e) => setNewLandmark(prev => ({ ...prev, distance: e.target.value }))}
                    className="px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="Distance"
                  />
                </div>
                <button
                  type="button"
                  onClick={addLandmark}
                  className="w-full px-2 py-1 bg-green-600 text-white rounded text-sm"
                >
                  Add Landmark
                </button>
              </div>
              <div className="space-y-2">
                {formData.nearestLandmarks.map((landmark, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div className="flex items-center space-x-2">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{landmark.location}</p>
                        <p className="text-xs text-gray-500">{landmark.distance}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeLandmark(index)}
                      className="p-1 text-red-500"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Amenities: structured search filters + optional tags for stay pages */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-5 space-y-6">
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-1">Amenities</h3>
                <p className="text-xs text-gray-500">
                  Set the options below for search filters. Add extra tags at the bottom for anything else to show on your listing.
                </p>
              </div>
              <ListingAttributesEditor
                value={formData.listingAttributes}
                onChange={(next) =>
                  setFormData((prev) => ({ ...prev, listingAttributes: next }))
                }
              />
              <div className="border-t border-gray-200 pt-5">
                <h4 className="text-sm font-medium text-gray-900 mb-1">Additional amenities</h4>
                <p className="text-xs text-gray-500 mb-3">
                  Short labels for the stay page (not used for structured search filters).
                </p>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newAmenity}
                    onChange={(e) => setNewAmenity(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addAmenity();
                      }
                    }}
                    className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm"
                    placeholder="e.g. Pet sitter on request"
                  />
                  <button
                    type="button"
                    onClick={addAmenity}
                    disabled={!newAmenity.trim()}
                    className="px-3 py-1.5 bg-green-600 text-white rounded text-sm disabled:opacity-50 shrink-0"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.amenities.map((amenity, index) => (
                    <div key={index} className="flex items-center space-x-1 bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                      <span className="text-xs">{amenity}</span>
                      <button
                        type="button"
                        onClick={() => removeAmenity(index)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Founder reviews carousel */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Founder reviews (property page carousel)
              </label>
              <p className="text-xs text-gray-500 mb-3">
                Pinboard on the listing: each slide is a sticky note with a quote and optional photo overlapping the bottom edge.
              </p>
              <div className="space-y-3">
                {(formData.founderReviews || []).map((item, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-3 space-y-2 bg-gray-50">
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-medium text-gray-700">Slide {index + 1}</span>
                      <button
                        type="button"
                        onClick={() =>
                          setFormData((prev) => ({
                            ...prev,
                            founderReviews: prev.founderReviews.filter((_, i) => i !== index)
                          }))
                        }
                        className="text-xs text-red-600"
                      >
                        Remove
                      </button>
                    </div>
                    <textarea
                      value={item.quote || ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        setFormData((prev) => {
                          const next = [...(prev.founderReviews || [])];
                          next[index] = { ...next[index], quote: v };
                          return { ...prev, founderReviews: next };
                        });
                      }}
                      rows={2}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                      placeholder="Quote text..."
                    />
                    <div className="flex flex-wrap items-start gap-2">
                      <div>
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/gif"
                          className="hidden"
                          id={`vendor-founder-review-upload-${index}`}
                          disabled={uploadingFounderReviewSlide === index}
                          onChange={(e) => handleFounderReviewImageUpload(index, e)}
                        />
                        <label
                          htmlFor={`vendor-founder-review-upload-${index}`}
                          className={`inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs text-gray-800 ${
                            uploadingFounderReviewSlide === index ? 'pointer-events-none opacity-60' : ''
                          }`}
                        >
                          <Upload className="h-3.5 w-3.5" />
                          {uploadingFounderReviewSlide === index ? 'Uploading…' : 'Upload photo'}
                        </label>
                      </div>
                      {item.image ? (
                        <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded border border-gray-200">
                          <ResponsiveImage
                            src={item.image}
                            variant="thumb64"
                            alt=""
                            className="h-full w-full object-cover object-center"
                          />
                          <button
                            type="button"
                            onClick={() => clearFounderReviewImage(index)}
                            className="absolute right-0 top-0 rounded-bl bg-black/60 p-0.5 text-white"
                            title="Remove"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ) : null}
                    </div>
                    <input
                      type="url"
                      value={item.image || ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        setFormData((prev) => {
                          const next = [...(prev.founderReviews || [])];
                          next[index] = { ...next[index], image: v };
                          return { ...prev, founderReviews: next };
                        });
                      }}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                      placeholder="Or paste image URL"
                    />
                    <input
                      type="text"
                      value={item.bubbleText || ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        setFormData((prev) => {
                          const next = [...(prev.founderReviews || [])];
                          next[index] = { ...next[index], bubbleText: v };
                          return { ...prev, founderReviews: next };
                        });
                      }}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                      placeholder="Bubble text (optional)"
                    />
                    <input
                      type="text"
                      value={item.borderColor || ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        setFormData((prev) => {
                          const next = [...(prev.founderReviews || [])];
                          next[index] = { ...next[index], borderColor: v };
                          return { ...prev, founderReviews: next };
                        });
                      }}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm font-mono"
                      placeholder="#FF5E4C"
                    />
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      founderReviews: [
                        ...(prev.founderReviews || []),
                        { quote: '', image: '', bubbleText: '', borderColor: '' }
                      ]
                    }))
                  }
                  className="text-sm text-[#FF5E4C] font-medium"
                >
                  + Add slide
                </button>
              </div>
            </div>

            {/* Settings */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Settings
              </label>
              <div className="space-y-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.founderVerified}
                    onChange={(e) => setFormData(prev => ({ ...prev, founderVerified: e.target.checked }))}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">Founder Verified</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.inSpotlight}
                    onChange={(e) => setFormData(prev => ({ ...prev, inSpotlight: e.target.checked }))}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">Featured in Spotlight</span>
                </label>
              </div>
            </div>
          </div>

          {/* Rooms Section */}
          {isEdit && (
            <div className="bg-white rounded-xl p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Rooms</h2>
                <button
                  type="button"
                  onClick={() => {
                    const stayGallery = Array.isArray(formData.images) ? [...formData.images] : [];
                    setRoomFormData({
                      stayId: formData.stayId,
                      roomId: `r_${Date.now().toString().slice(-8)}`,
                      title: '',
                      description: '',
                      capacity: 2,
                      beds: 1,
                      features: [],
                      options: { ...DEFAULT_ROOM_FORM_OPTIONS },
                      price: {
                        mrp: 0,
                        discountedPrice: 0,
                        taxes: 0,
                        currency: 'INR',
                        useDayOfWeekPricing: false,
                        dayPricing: {}
                      },
                      images: stayGallery,
                      amenities: []
                    });
                    setEditingRoom(null);
                    setShowRoomForm(true);
                  }}
                  className="text-[#FF5E4C] text-sm font-medium hover:opacity-90"
                >
                  Add room
                </button>
              </div>

              {rooms.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500 mb-4">No rooms added yet</p>
                  <button
                    type="button"
                    onClick={() => {
                      const stayGallery = Array.isArray(formData.images) ? [...formData.images] : [];
                      setRoomFormData({
                        stayId: formData.stayId,
                        roomId: `r_${Date.now().toString().slice(-8)}`,
                        title: '',
                        description: '',
                        capacity: 2,
                        beds: 1,
                        features: [],
                        options: { ...DEFAULT_ROOM_FORM_OPTIONS },
                        price: {
                          mrp: 0,
                          discountedPrice: 0,
                          taxes: 0,
                          currency: 'INR',
                          useDayOfWeekPricing: false,
                          dayPricing: {}
                        },
                        images: stayGallery,
                        amenities: []
                      });
                      setShowRoomForm(true);
                    }}
                    className="px-4 py-2 bg-[#FF5E4C] text-white rounded-lg"
                  >
                    Add Room
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {(showRooms ? rooms : rooms.slice(0, 3)).map((room) => (
                    <div
                      key={room.roomId}
                      onClick={() => handleEditRoom(room)}
                      className="flex items-start space-x-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50"
                    >
                      <div className="w-20 h-20 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                        {room.images && room.images.length > 0 ? (
                          <ResponsiveImage
                            src={room.images[0]}
                            variant="thumb80"
                            alt={room.title}
                            className="h-full w-full object-cover object-center"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Bed className="w-6 h-6 text-gray-400" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-gray-900 mb-1">{room.title}</h3>
                        <div className="flex items-center space-x-4 text-sm text-gray-600 mb-2">
                          <span className="flex items-center">
                            <Bed className="w-4 h-4 mr-1" />
                            {room.beds} {room.beds === 1 ? 'Bed' : 'Beds'}
                          </span>
                          <span>Sleeps {room.capacity}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-lg font-bold text-[#FF5E4C]">
                              ₹
                              {minDiscountedAcrossWeek(room).toLocaleString('en-IN')}
                            </p>
                            {room.price?.mrp && room.price.mrp > minDiscountedAcrossWeek(room) && (
                              <p className="text-sm text-gray-500 line-through">
                                ₹{room.price.mrp.toLocaleString('en-IN')}
                              </p>
                            )}
                            {roomTaxesFromDiscounted(room.price?.discountedPrice) > 0 && (
                              <p className="text-xs text-gray-500">
                                Includes ₹{roomTaxesFromDiscounted(room.price.discountedPrice).toLocaleString('en-IN')} taxes & fees
                              </p>
                            )}
                          </div>
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        </div>
                      </div>
                    </div>
                  ))}
                  {rooms.length > 3 && (
                    <button
                      type="button"
                      onClick={() => setShowRooms(!showRooms)}
                      className="w-full py-2 text-[#FF5E4C] text-sm font-medium hover:opacity-90"
                    >
                      {showRooms ? 'Show less' : `View all ${rooms.length} rooms`}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Submit Button */}
          <div className="flex space-x-4 pb-4 md:pb-8">
            <button
              type="button"
              onClick={() => navigate('/vendor/listings')}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-3 bg-[#FF5E4C] text-white rounded-lg disabled:opacity-50 flex items-center justify-center space-x-2"
            >
              <Save className="w-4 h-4" />
              <span>{loading ? 'Saving...' : 'Save'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VendorListingDetails;
